import { useEffect, useState } from 'react';

import { menuPageService } from 'features/menu-page/infra';

import type {
  FullMenuCatalog,
  MenuOutOfStock,
} from 'features/menu-page/domain';
import type { Store, StoreId } from 'shared/domain';

const INIT_STORE: Store = {
  id: NaN,
  name: '',
} as const;

const INIT_CATALOG: FullMenuCatalog = {
  categories: [],
  products: [],
  customizationGroups: [],
};

const INIT_OUT_OF_STOCK: MenuOutOfStock = {
  quantity: 0,
  products: [],
  customizations: [],
};

export function useMenuPage() {
  const [store, setStore] = useState(INIT_STORE);
  const [isMigrated, setIsMigrated] = useState(false);
  const [catalog, setCatalog] = useState(INIT_CATALOG);
  const [outOfStock, setOutOfStock] = useState(INIT_OUT_OF_STOCK);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoadingCatalog, setIsLoadingCatalog] = useState(false);

  const {
    getStoresByProfile,
    getMigrationStatus,
    getFullMenuCatalog,
    validateOutOfStock,
  } = menuPageService();

  const stores = getStoresByProfile();

  const refreshCatalog = (params?: { isNew?: boolean }) => {
    const { isNew } = params ?? {};
    const _isMigrated = isNew ?? isMigrated;
    getFullMenuCatalog({ storeId: store.id, isMigrated: _isMigrated }).then(
      _fm => {
        setCatalog(_fm);
        setIsLoadingCatalog(false);
        validateOutOfStock({
          catalog: _fm,
          storeId: store.id,
          isMigrated: _isMigrated,
        }).then(setOutOfStock);
      },
    );
  };

  const changeStore = (storeId: StoreId) => {
    setStore(stores.find(_s => _s.id === storeId) ?? INIT_STORE);
  };

  const onSearch = (value: string) => setSearchQuery(value);

  useEffect(() => {
    setStore(_s => (stores.length > 0 && isNaN(store.id) ? stores[0] : _s));
  }, [stores]);

  useEffect(() => {
    if (!isNaN(store.id)) {
      getMigrationStatus({ storeId: store.id }).then(isNew => {
        setIsLoadingCatalog(true);
        setIsMigrated(isNew);
        refreshCatalog({ isNew });
      });
    }
  }, [store]);

  return {
    isLoadingCatalog,
    store,
    stores,
    catalog,
    outOfStock,
    searchQuery,
    refreshCatalog,
    changeStore,
    onSearch,
  };
}
